import { Droppable } from "@hello-pangea/dnd";
import { AddRounded, MoreVertRounded } from "@mui/icons-material";
import { Box, IconButton, Menu, MenuItem, Stack, styled, Typography, useTheme } from "@mui/material";
import React, { useState } from "react";
import {
  Accordion,
  AccordionTitle,
  ActionItemCard,
  ActionItemsTable,
  CreateActionItemModal,
  ViewList,
} from "../../../../components";
import { useSession } from "../../../../hooks";
import { useDeleteSection } from "../../../../http";
import { IBlock, ISection } from "../../../../models";
import { useDestructiveConfirm } from "../../../../utilities";
import { EditSectionModal } from "./EditSectionModal";
import { usePlanContext } from "./PlanContext";

const DroppableZone = styled(Stack)<{ $isDraggingOver: boolean }>`
  transition: ${({ theme }) => theme.transitions.create("background-color")};
  position: relative;

  background-color: ${({ $isDraggingOver, theme }) =>
    $isDraggingOver ? theme.palette.primary.opacity5 : "transparent"};
  pointer-events: ${({ $isDraggingOver }) => ($isDraggingOver ? "none" : "auto")};

  &::before,
  &::after {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    height: 20px;
    opacity: ${({ $isDraggingOver }) => ($isDraggingOver ? 1 : 0)};
    transition: ${({ theme }) => theme.transitions.create("opacity")};
    pointer-events: none;
    z-index: 0;
  }

  &::before {
    bottom: 100%;
    background: linear-gradient(to top, ${({ theme }) => theme.palette.primary.opacity5}, transparent);
  }

  &::after {
    top: 100%;
    background: linear-gradient(to bottom, ${({ theme }) => theme.palette.primary.opacity5}, transparent);
  }
`;

interface IProps {
  teamName: string;
  teamSlug: string;
  section: ISection;
  block: IBlock;
  isPastView: boolean;
  canEditPlan: boolean;
  isTableView: boolean;
  onEditPlanStructureClicked: () => void;
}

const Section = (props: IProps) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const destructiveConfirm = useDestructiveConfirm();
  const { mutate: deleteSection } = useDeleteSection();
  const user = useSession();
  const { planGuid } = usePlanContext();
  const [isEditSectionModalVisible, setIsEditSectionModalVisible] = useState(false);
  const [isCreateActionItemModalVisible, setIsCreateActionItemModalVisible] = useState(false);
  const [focusedActionItemGuid, setFocusedActionItemGuid] = useState<string | undefined>(undefined);
  const { fetchActionItemSubitems } = usePlanContext();
  const theme = useTheme();

  function handleMenuItemClicked(e: React.MouseEvent) {
    setMenuAnchor(null);
    e.stopPropagation();
  }

  async function handleAddActionItemClicked(e: React.MouseEvent) {
    setIsCreateActionItemModalVisible(true);
    e.stopPropagation();
  }

  async function handleEditClicked(e: React.MouseEvent) {
    handleMenuItemClicked(e);
    setIsEditSectionModalVisible(true);
  }

  async function handleDeleteClicked(e: React.MouseEvent) {
    handleMenuItemClicked(e);

    const { confirmed } = await destructiveConfirm({
      title: "Delete Section",
      content: "Deleting this section will delete all of its items as well.",
      confirmationText: "Delete",
      cancellationText: "Cancel",
    });

    if (!confirmed) {
      return;
    }

    deleteSection({
      planGuid: planGuid,
      guid: props.section.guid,
      modifiedByUserId: user.userId,
    });
  }

  return (
    <Stack>
      <Droppable key={props.section.guid} droppableId={props.section.guid}>
        {(provided, snapshot) => (
          <Accordion
            title={(expanded) => (
              <AccordionTitle
                title={props.section.name}
                subtitle={expanded ? undefined : `(${props.section.actionItems.length})`}
              />
            )}
            hintText={props.section.hintText}
            hoverActions={
              !snapshot.isDraggingOver &&
              props.canEditPlan &&
              (props.section.isEditable || props.section.createActionItemAllowed) ? (
                <Stack direction="row" spacing={0.25}>
                  {props.section.createActionItemAllowed && (
                    <IconButton size="small" onClick={handleAddActionItemClicked}>
                      <AddRounded />
                    </IconButton>
                  )}
                  {props.section.isEditable && (
                    <>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setMenuAnchor(e.currentTarget);
                        }}
                      >
                        <MoreVertRounded />
                      </IconButton>
                      <Menu
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={(e: React.PointerEvent) => {
                          setMenuAnchor(null);
                          e.stopPropagation();
                        }}
                      >
                        <MenuItem onClick={handleEditClicked}>Edit Section</MenuItem>
                        <MenuItem
                          onClick={(e) => {
                            handleMenuItemClicked(e);
                            props.onEditPlanStructureClicked();
                          }}
                        >
                          Reorder Blocks And Sections
                        </MenuItem>
                        <MenuItem onClick={handleDeleteClicked} sx={{ color: theme.palette.error.main }}>
                          Delete Section
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </Stack>
              ) : undefined
            }
            expanded={snapshot.isDraggingOver ? true : undefined}
            color={props.isPastView ? theme.palette.grey[400] : theme.palette.primary.main}
            backgroundColor={theme.palette.grey[100]}
            accordionDetailsSx={{ pb: props.isTableView ? 0 : 0.5 }}
          >
            <DroppableZone
              $isDraggingOver={snapshot.isDraggingOver}
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {props.isTableView ? (
                <ActionItemsTable
                  tableId={`Section-${props.section.guid}`}
                  actionItems={[...props.section.actionItems].sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))}
                  placeholder={
                    <Box sx={{ mx: 1, my: 0.5 }}>
                      <Typography variant="placeholder">Nothing here yet.</Typography>
                    </Box>
                  }
                  isPastView={props.isPastView}
                  canEditPlan={props.canEditPlan}
                  fetchActionItemSubitems={fetchActionItemSubitems}
                />
              ) : (
                <ViewList
                  dataSource={[...props.section.actionItems].sort((a, b) => a.sortOrder.localeCompare(b.sortOrder))}
                  spacing={0.5}
                  renderItem={(actionItem, index) => (
                    <ActionItemCard
                      key={actionItem.guid}
                      draggable
                      index={index}
                      actionItemGuid={actionItem.guid}
                      canEdit={actionItem.userCanEdit && !props.isPastView}
                      canEditPlan={props.canEditPlan}
                      isPastView={props.isPastView}
                      isFocused={actionItem.guid === focusedActionItemGuid}
                      onBlur={() => setFocusedActionItemGuid(undefined)}
                    />
                  )}
                  sx={{ px: 0.5 }}
                />
              )}
              {provided.placeholder}
            </DroppableZone>
          </Accordion>
        )}
      </Droppable>

      <EditSectionModal
        visible={isEditSectionModalVisible}
        section={props.section}
        blockGuid={props.block.guid}
        onSaved={() => setIsEditSectionModalVisible(false)}
        onCancelled={() => setIsEditSectionModalVisible(false)}
      />

      <CreateActionItemModal
        visible={isCreateActionItemModalVisible}
        teamPlanSection={{
          teamName: props.teamName,
          teamSlug: props.teamSlug,
          planGuid: planGuid,
          sectionName: props.section.name,
          sectionGuid: props.section.guid,
        }}
        onCreated={() => {
          setIsCreateActionItemModalVisible(false);
        }}
        onCancelled={() => setIsCreateActionItemModalVisible(false)}
      />
    </Stack>
  );
};

export { Section };
